import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Activity, CircleHelp, Lock, LogOut, Play, Unlock, Orbit } from "lucide-react";
import { logout } from "../lib/api";

const fmt = (s) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const PHASES = {
  P0: "Calibration",
  P1: "Baseline",
  P2: "Needs Seed",
  P3: "Tier Fill",
  P4: "Business Probe",
  P5: "Synthesis",
};

export const TelemetryBar = ({ session, elapsed }) => {
  const loc = useLocation();
  const nav = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const pct = Math.min(100, (elapsed / (session?.test_window_seconds || 28800)) * 100);
  const nav_items = [
    { to: "/interview", label: "Interview" },
    { to: "/sphere", label: "Sphere" },
    { to: "/debrief", label: "Results" },
  ];
  if (session?.user?.is_operator) nav_items.push({ to: "/operator", label: "Operator" });

  const signOut = async () => {
    await logout();
    nav("/", { replace: true });
  };

  return (
    <header
      data-testid="telemetry-bar"
      className="relative z-30 flex shrink-0 flex-wrap items-center gap-x-6 gap-y-2 border-b border-[var(--line)] bg-[rgba(5,5,5,0.86)] px-4 py-2 backdrop-blur-xl sm:px-6"
    >
      <div className="flex items-center gap-2.5">
        <Orbit size={14} className="text-zinc-400" strokeWidth={1.4} />
        <span className="font-display text-base leading-none tracking-tight">
          Covalence&nbsp;Sphere
        </span>
      </div>

      <div className="hidden h-4 w-px bg-[var(--line-hi)] sm:block" />

      <nav className="flex items-center gap-4">
        {nav_items.map((n) => (
          <Link
            key={n.to}
            to={n.to}
            data-testid={`nav-${n.label.toLowerCase()}`}
            className={`label transition-colors hover:text-zinc-100 ${
              loc.pathname === n.to ? "!text-zinc-100" : ""
            }`}
          >
            {n.label}
          </Link>
        ))}
      </nav>

      <div className="ml-auto flex items-center gap-5">
        <div data-testid="telemetry-tier-badge" className="hidden sm:block">
          <span className="label">Phase</span>
          <span className="ml-2 font-data text-[11px] text-zinc-200">
            {session?.phase || "P0"} · {PHASES[session?.phase] || "Calibration"}
          </span>
        </div>

        <div className="hidden md:block">
          <span className="label">Coverage</span>
          <span className="ml-2 font-data text-[11px] text-zinc-200">
            {session?.coverage?.filled ?? 0}/{session?.coverage?.total ?? 46}
          </span>
        </div>

        <div data-testid="telemetry-honesty-signal" className="flex items-center gap-2">
          <Activity size={12} className="text-zinc-500" strokeWidth={1.5} />
          <span className="label">Honesty</span>
          <span className="font-data text-[11px] text-zinc-100">
            {session ? Math.round(session.honesty_signal) : 0}
          </span>
          <div className="h-[3px] w-16 bg-[var(--line)]">
            <div
              className="h-full bg-zinc-100 transition-all duration-700"
              style={{ width: `${session?.honesty_signal || 0}%` }}
            />
          </div>
          <span className="hidden font-data text-[10px] text-zinc-500 lg:inline">
            {session?.honesty_trend || "establishing"}
          </span>
        </div>

        <div className="flex items-center gap-2" data-testid="telemetry-gate">
          {session?.gate_open ? (
            <Unlock size={12} className="text-zinc-100" strokeWidth={1.5} />
          ) : (
            <Lock size={12} className="text-zinc-500" strokeWidth={1.5} />
          )}
          <span className="label">{session?.gate_open ? "Gate open" : "Sealed"}</span>
        </div>

        <div data-testid="telemetry-elapsed" className="flex items-center gap-2">
          {session?.timer?.state && session.timer.state !== "running" && (
            <span className="label !text-zinc-200" data-testid="telemetry-timer-state">
              {session.timer.state}
            </span>
          )}
          <span
            className={`font-data text-[11px] tabular-nums ${
              session?.timer?.state === "running" ? "text-zinc-300" : "text-zinc-500"
            }`}
          >
            {fmt(elapsed)}
          </span>
          <div className="h-[3px] w-14 bg-[var(--line)]">
            <div className="h-full bg-zinc-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <span className="label hidden xl:inline">/ {fmt(session?.test_window_seconds || 28800)}</span>
        </div>

        <div className="relative">
          <button
            data-testid="help-btn"
            title="Guide and walkthrough"
            onClick={() => setHelpOpen((v) => !v)}
            className="label flex items-center gap-1.5 transition-colors hover:text-zinc-100"
          >
            <CircleHelp size={13} strokeWidth={1.6} />
          </button>
          {helpOpen && (
            <>
              <button
                aria-label="close menu"
                data-testid="help-menu-backdrop"
                onClick={() => setHelpOpen(false)}
                className="fixed inset-0 z-30 cursor-default"
              />
              <div data-testid="help-menu" className="panel absolute right-0 z-40 mt-2 w-52 p-1">
                <Link
                  to="/help"
                  data-testid="help-menu-guide"
                  onClick={() => setHelpOpen(false)}
                  className="label block px-3 py-2.5 hover:bg-[rgba(255,255,255,0.04)] hover:!text-zinc-100"
                >
                  Guide
                </Link>
                <button
                  data-testid="help-menu-replay"
                  onClick={() => {
                    setHelpOpen(false);
                    window.dispatchEvent(new Event("covalence:replay-tour"));
                  }}
                  className="label flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-[rgba(255,255,255,0.04)] hover:!text-zinc-100"
                >
                  <Play size={9} strokeWidth={2} /> Replay walkthrough
                </button>
              </div>
            </>
          )}
        </div>

        <button
          onClick={signOut}
          data-testid="logout-btn"
          title={session?.user?.email || "Sign out"}
          className="label flex items-center gap-1.5 transition-colors hover:text-zinc-100"
        >
          <LogOut size={11} strokeWidth={1.6} />
          <span className="hidden lg:inline">
            {session?.user?.name?.split(" ")[0] || "Exit"}
          </span>
        </button>
      </div>
    </header>
  );
};

export default TelemetryBar;
